// src/components/pages/admin-dash/ScratchSettingsAdmin.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Spinner from "../../ui/Spinner";

/**
 * Site-wide on/off switch for the Scratch-Off Intelligence feature, backed
 * by the same single-row `globalconfiguration` GET/PUT used by
 * GlobalConfigurationAdmin.jsx — this screen just surfaces one field of it
 * (`scratchEnabled`) so it's a menu of its own instead of buried in the
 * general Global config screen.
 *
 * When off, the feature is hidden everywhere on the live site — nav,
 * Landing marketing/FAQ, and the `/scratch*` routes/API — for anonymous
 * visitors and logged-in users alike. The admin panel (this screen and
 * the Scratch-Offs data-source/ingest views) is never affected by it.
 */
export default function ScratchSettingsAdmin({ onShowToast }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [scratchEnabled, setScratchEnabled] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/admin/global-configuration");
      setConfig(res.data);
      setScratchEnabled(!!res.data?.scratchEnabled);
    } catch {
      setError("Couldn't load Scratch-Off settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const dirty = config && scratchEnabled !== !!config.scratchEnabled;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axiosInstance.put("/admin/global-configuration", {
        noReplyEmail: config.noReplyEmail,
        fromEmail: config.fromEmail,
        fromName: config.fromName,
        webAddress: config.webAddress,
        alertRecipientEmails: config.alertRecipientEmails,
        weeklyReportEnabled: config.weeklyReportEnabled,
        scratchEnabled,
      });
      setConfig(res.data);
      onShowToast?.("Scratch-Off settings updated.", "success");
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Failed to save settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Scratch-Off settings
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Site-wide switch for the Scratch-Off Intelligence feature. Takes effect
          immediately — no restart needed. This never hides the feature from the
          admin panel, only from the public site and logged-in users.
        </p>
      </header>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={18} />
          </div>
        ) : config ? (
          <form onSubmit={handleSave} className="space-y-1">
            <label className="flex items-center gap-2 text-[13px] mb-3.5 mt-1" style={{ color: "var(--txt)" }}>
              <input
                type="checkbox"
                checked={scratchEnabled}
                onChange={(e) => setScratchEnabled(e.target.checked)}
              />
              Show Scratch-Off Intelligence on the public site
            </label>

            <div className="flex items-center justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <Button icon="check" size="sm" type="submit" loading={saving} disabled={!dirty}>
                Save changes
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-[13px] py-4 text-center" style={{ color: "var(--txt-2)" }}>
            No configuration row found.
          </p>
        )}
      </Card>
    </div>
  );
}
